import { Box, Typography } from '@mui/material';

import Modal from './Modal';
import { useModal } from '../../../context/AccountModalContext';
import { useSubscriptionAccess } from '../../../context/SubscriptionAccessContext';
import { useNavigate } from 'react-router-dom';

/**
 * A modal component that displays upgrade information and options to users
 * based on their current subscription plan. It shows a reminder for trial
 * users to upgrade, and informs monthly and annual subscribers about their
 * renewal status. The modal includes options to upgrade the subscription or
 * dismiss the reminder.
 *
 * @returns {JSX.Element|null} The rendered modal component if subscription
 * data is available; otherwise, null.
 */
const UpgradeModal = () => {
    const { subscriptionData, loading } = useSubscriptionAccess();
    const { showUpgradeModal, toggleUpgradeModal } = useModal();
    const navigate = useNavigate();

    if (loading || !subscriptionData) return null;

    const handleRemindLater = () => {
        const today = new Date().toDateString();
        localStorage.setItem('trialDismissedOn', today);
        toggleUpgradeModal();
    };

    const handleUpgrade = () => {
        toggleUpgradeModal();
        navigate('/settings/billing/plans?plan=annual');
    };

    const modalLogo = (
        <Box
            component="img"
            src="/assets/images/Toolkit.law-Your-Legal-Global-Partner.svg"
            width="auto"
            height="50px"
            alt="Toolkit.law Logo"
        />
    );

    const sxBody = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        pb: 1,
    };

    const modalBody = (
        <>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                {subscriptionData?.subscriptionName} Expiring Soon
            </Typography>

            {subscriptionData?.isTrial ? (
                <Typography gutterBottom>
                    Your trial will expire in {subscriptionData?.daysLeft} days.
                    Please upgrade your account to continue enjoying all
                    features.
                </Typography>
            ) : subscriptionData?.isMonthly ? (
                <Typography gutterBottom>
                    Your {subscriptionData?.subscriptionName} will renew
                    automatically in {subscriptionData?.daysLeft} days. Your
                    payment will be charged unless you cancel before then. You
                    can also upgrade to an Annual plan to save more.
                </Typography>
            ) : (
                <Typography gutterBottom>
                    Your {subscriptionData?.subscriptionName} will renew
                    automatically in {subscriptionData?.daysLeft} days. Your
                    payment will be charged unless you cancel before then.
                </Typography>
            )}
        </>
    );

    const footerButtons = subscriptionData?.isTrial
        ? [
              {
                  label: 'Upgrade',
                  onClick: handleUpgrade,
              },
              {
                  label: 'Remind Me Later',
                  onClick: handleRemindLater,
                  variant: 'dark',
              },
          ]
        : subscriptionData?.isMonthly
        ? [
              {
                  label: 'Upgrade to Annual',
                  onClick: handleUpgrade,
              },
              {
                  label: 'Remind Me Later',
                  onClick: handleRemindLater,
                  variant: 'dark',
              },
          ]
        : [
              {
                  label: 'Close',
                  onClick: handleRemindLater,
                  variant: 'dark',
              },
          ];

    return (
        <Modal
            show={showUpgradeModal}
            size="xs"
            modalLogo={modalLogo}
            sxBody={sxBody}
            modalBody={modalBody}
            footerButtons={footerButtons}
        />
    );
};

export default UpgradeModal;
